import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DashboardDeliveryRulesSchema,
  DashboardRestaurantListSchema,
  DeliverySuggestionsSchema,
  type DashboardDeliveryRule,
  type DashboardRestaurant,
} from "@bytebite/schemas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { apiGet, apiPut, ApiError } from "@/lib/api";
import { eurosToCents, formatEuro } from "@/lib/money";
import { readStoredRestaurantId, storeRestaurantId } from "@/lib/restaurant-context";

const fieldClass =
  "border-input bg-background w-full rounded-md border px-3 py-2 text-sm shadow-sm";

const SCOPES = ["CITY", "POSTAL_CODE_CITY", "DISTRICT", "STREET"] as const;
type Scope = (typeof SCOPES)[number];

const SCOPE_LABEL: Record<Scope, string> = {
  CITY: "City",
  POSTAL_CODE_CITY: "Postal code",
  DISTRICT: "District",
  STREET: "Street",
};

type ZoneDraft =
  | {
      key: string;
      kind: "saved";
      id: string;
      scope: Scope;
      label: string;
      minOrderCents: number;
      deliveryFeeCents: number;
      freeDeliverySubtotalCents: number | null;
      freeDeliveryNone: boolean;
      active: boolean;
    }
  | {
      key: string;
      kind: "new";
      providerId: string;
      sessionToken: string;
      scope: Scope;
      label: string;
      minOrderCents: number;
      deliveryFeeCents: number;
      freeDeliverySubtotalCents: number | null;
      freeDeliveryNone: boolean;
      active: boolean;
    };

function zoneLabel(
  rule: Pick<DashboardDeliveryRule, "scope" | "city" | "postalCode" | "district" | "street">,
): string {
  if (rule.scope === "STREET") return `${rule.street}, ${rule.city}`;
  if (rule.scope === "DISTRICT") return `${rule.district}, ${rule.city}`;
  if (rule.scope === "POSTAL_CODE_CITY") return `${rule.postalCode} ${rule.city}`;
  return rule.city;
}

function fromSaved(rule: DashboardDeliveryRule): ZoneDraft {
  return {
    key: rule.id,
    kind: "saved",
    id: rule.id,
    scope: rule.scope,
    label: zoneLabel(rule),
    minOrderCents: rule.minOrderCents,
    deliveryFeeCents: rule.deliveryFeeCents,
    freeDeliverySubtotalCents: rule.freeDeliverySubtotalCents,
    freeDeliveryNone: rule.freeDeliveryNone,
    active: rule.active,
  };
}

function freeDeliveryText(
  zone: Pick<ZoneDraft, "freeDeliverySubtotalCents" | "freeDeliveryNone">,
): string {
  if (zone.freeDeliveryNone) return "no free delivery";
  if (zone.freeDeliverySubtotalCents === null) return "free from: inherited";
  return `free from ${formatEuro(zone.freeDeliverySubtotalCents)}`;
}

function newSessionToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  hex[6] = (((bytes.at(6) ?? 0) & 0x0f) | 0x40).toString(16).padStart(2, "0");
  hex[8] = (((bytes.at(8) ?? 0) & 0x3f) | 0x80).toString(16).padStart(2, "0");
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}

export function DeliveryPage() {
  const [restaurants, setRestaurants] = useState<DashboardRestaurant[]>([]);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [zones, setZones] = useState<ZoneDraft[]>([]);
  const [sessionToken] = useState(newSessionToken);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<{ id: string; label: string }[]>([]);
  const [picked, setPicked] = useState<{ id: string; label: string } | null>(null);
  const [scope, setScope] = useState<Scope>("CITY");
  const [minInput, setMinInput] = useState("15,00");
  const [feeInput, setFeeInput] = useState("2,50");
  const [freeInput, setFreeInput] = useState("");
  const [noFreeDelivery, setNoFreeDelivery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const selected = useMemo(
    () => restaurants.find((restaurant) => restaurant.id === restaurantId) ?? null,
    [restaurantId, restaurants],
  );

  const loadZones = useCallback(async (id: string) => {
    const payload = DashboardDeliveryRulesSchema.parse(
      await apiGet<unknown>(`/dashboard/restaurants/${id}/delivery-rules`),
    );
    setZones(payload.rules.map(fromSaved));
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      try {
        const list = DashboardRestaurantListSchema.parse(
          await apiGet<unknown>("/dashboard/restaurants"),
        );
        if (cancelled) return;
        setRestaurants(list.restaurants);
        const next = readStoredRestaurantId(list.restaurants[0]?.id ?? null);
        const chosen = list.restaurants.some((restaurant) => restaurant.id === next)
          ? next
          : (list.restaurants[0]?.id ?? null);
        setRestaurantId(chosen);
        if (chosen) {
          storeRestaurantId(chosen);
          await loadZones(chosen);
        }
      } catch {
        if (!cancelled) setError("Could not load delivery zones.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
  }, [loadZones]);

  useEffect(() => {
    if (!restaurantId || query.trim().length < 2 || picked) {
      return;
    }
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({ q: query.trim(), sessionToken });
      void apiGet<unknown>(
        `/dashboard/restaurants/${restaurantId}/delivery-suggestions?${params.toString()}`,
      )
        .then((body) => {
          const parsed = DeliverySuggestionsSchema.parse(body);
          setSuggestions(parsed.suggestions);
          setError(null);
        })
        .catch((cause) => {
          setSuggestions([]);
          setError(
            cause instanceof ApiError && cause.status === 503
              ? "Address search is not available."
              : "Address search failed.",
          );
        });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [picked, query, restaurantId, sessionToken]);

  async function changeRestaurant(id: string) {
    setRestaurantId(id);
    storeRestaurantId(id);
    setError(null);
    setPicked(null);
    setQuery("");
    setSuggestions([]);
    try {
      await loadZones(id);
    } catch {
      setError("Could not load these delivery zones.");
    }
  }

  function addZone() {
    if (!picked) {
      setError("Choose an address from the search results.");
      return;
    }
    const minOrderCents = eurosToCents(minInput);
    const deliveryFeeCents = eurosToCents(feeInput);
    if (minOrderCents === null || deliveryFeeCents === null) {
      setError("Enter the minimum and fee in euros.");
      return;
    }
    const freeText = freeInput.trim();
    const freeDeliverySubtotalCents = freeText === "" ? null : eurosToCents(freeText);
    if (freeText !== "" && freeDeliverySubtotalCents === null) {
      setError("Enter the free-delivery threshold in euros, or leave it empty.");
      return;
    }
    if (noFreeDelivery && freeDeliverySubtotalCents !== null) {
      setError("Either set a free-delivery threshold or switch free delivery off.");
      return;
    }
    setZones((current) => [
      ...current,
      {
        key: `${picked.id}:${scope}`,
        kind: "new",
        providerId: picked.id,
        sessionToken,
        scope,
        label: picked.label,
        minOrderCents,
        deliveryFeeCents,
        freeDeliverySubtotalCents,
        freeDeliveryNone: noFreeDelivery,
        active: true,
      },
    ]);
    setFreeInput("");
    setNoFreeDelivery(false);
    setPicked(null);
    setQuery("");
    setSuggestions([]);
    setError(null);
  }

  async function save() {
    if (!restaurantId) return;
    setBusy(true);
    setError(null);
    try {
      const saved = DashboardDeliveryRulesSchema.parse(
        await apiPut<unknown>(`/dashboard/restaurants/${restaurantId}/delivery-rules`, {
          rules: zones.map((zone) =>
            zone.kind === "saved"
              ? {
                  id: zone.id,
                  minOrderCents: zone.minOrderCents,
                  deliveryFeeCents: zone.deliveryFeeCents,
                  freeDeliverySubtotalCents: zone.freeDeliverySubtotalCents,
                  freeDeliveryNone: zone.freeDeliveryNone,
                  active: zone.active,
                }
              : {
                  providerId: zone.providerId,
                  sessionToken: zone.sessionToken,
                  scope: zone.scope,
                  minOrderCents: zone.minOrderCents,
                  deliveryFeeCents: zone.deliveryFeeCents,
                  freeDeliverySubtotalCents: zone.freeDeliverySubtotalCents,
                  freeDeliveryNone: zone.freeDeliveryNone,
                  active: zone.active,
                },
          ),
        }),
      );
      setZones(saved.rules.map(fromSaved));
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 503) {
        setError("Address search is not configured.");
      } else if (cause instanceof ApiError && cause.status === 400) {
        setError("A zone could not be saved. Pick the address again and check the scope.");
      } else {
        setError("Delivery zones could not be saved.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <p className="text-muted-foreground text-sm">Loading delivery zones…</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Delivery</h1>
          <p className="text-muted-foreground text-sm">
            Zones for {selected?.name ?? "the selected restaurant"}. The most specific match wins.
          </p>
        </div>
        {restaurants.length > 0 ? (
          <label className="flex w-full max-w-xs flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Restaurant</span>
            <select
              className={fieldClass}
              value={restaurantId ?? ""}
              onChange={(event) => void changeRestaurant(event.target.value)}
            >
              {restaurants.map((restaurant) => (
                <option key={restaurant.id} value={restaurant.id}>
                  {restaurant.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {error ? <p className="text-sm text-red-700">{error}</p> : null}

      <Card>
        <CardHeader>
          <CardTitle>Add a zone</CardTitle>
          <CardDescription>
            Search for a place, then choose how specific the rule is. Saving reads the address from
            the provider so it matches checkout.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            Address
            <input
              className={fieldClass}
              value={query}
              placeholder="City, district, or street"
              onChange={(event) => {
                setQuery(event.target.value);
                setPicked(null);
              }}
            />
          </label>
          {suggestions.length > 0 ? (
            <ul className="overflow-hidden rounded-md border">
              {suggestions.map((suggestion) => (
                <li key={suggestion.id}>
                  <button
                    type="button"
                    className="hover:bg-muted w-full px-3 py-2 text-left text-sm"
                    onClick={() => {
                      setPicked(suggestion);
                      setQuery(suggestion.label);
                      setSuggestions([]);
                    }}
                  >
                    {suggestion.label}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-4">
            <label className="flex flex-col gap-1 text-sm">
              Scope
              <select
                className={fieldClass}
                value={scope}
                onChange={(event) => setScope(event.target.value as Scope)}
              >
                {SCOPES.map((value) => (
                  <option key={value} value={value}>
                    {SCOPE_LABEL[value]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Minimum (€)
              <input
                className={fieldClass}
                value={minInput}
                onChange={(event) => setMinInput(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Fee (€)
              <input
                className={fieldClass}
                value={feeInput}
                onChange={(event) => setFeeInput(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Free from (€)
              <input
                className={fieldClass}
                value={freeInput}
                placeholder="inherit"
                disabled={noFreeDelivery}
                onChange={(event) => setFreeInput(event.target.value)}
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={noFreeDelivery}
              onChange={(event) => {
                setNoFreeDelivery(event.target.checked);
                if (event.target.checked) setFreeInput("");
              }}
            />
            No free delivery in this zone
          </label>
          <p className="text-muted-foreground text-xs">
            Leave &ldquo;Free from&rdquo; empty to inherit the threshold of the next larger zone. 0
            means always free. Fee and minimum never inherit.
          </p>
          <Button type="button" variant="outline" onClick={addZone} disabled={!picked}>
            Add zone
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Zones</CardTitle>
          <CardDescription>
            {zones.length === 0
              ? "No zones yet. Pickup stays available."
              : zones.length === 1
                ? "1 zone"
                : `${zones.length} zones`}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {zones.map((zone) => (
            <div
              key={zone.key}
              className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center"
            >
              <div className="flex-1 text-sm">
                <p className="font-medium">{zone.label}</p>
                <p className="text-muted-foreground">
                  {SCOPE_LABEL[zone.scope]} · min {formatEuro(zone.minOrderCents)} · fee{" "}
                  {formatEuro(zone.deliveryFeeCents)} · {freeDeliveryText(zone)}
                  {zone.kind === "new" ? " · unsaved" : ""}
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={zone.active}
                  onChange={(event) =>
                    setZones((current) =>
                      current.map((item) =>
                        item.key === zone.key ? { ...item, active: event.target.checked } : item,
                      ),
                    )
                  }
                />
                Active
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setZones((current) => current.filter((item) => item.key !== zone.key))
                }
              >
                Remove
              </Button>
            </div>
          ))}
          <div>
            <Button type="button" disabled={busy || !restaurantId} onClick={() => void save()}>
              {busy ? "Saving…" : "Save zones"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
